import { useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ArrowLeft, CheckCircle, Mail, KeyRound, Lock, MessageCircle } from "lucide-react";

export default function ForgotPasswordPage() {
  const [step, setStep] = useState("email"); // email | code | success
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [merchantPhone, setMerchantPhone] = useState("");

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/request-reset", { email });
      setMerchantPhone(data.merchant_phone || "");
      setStep("code");
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la demande");
    } finally { setLoading(false); }
  };

  const openWhatsAppForCode = () => {
    if (!merchantPhone) return;
    const cleanPhone = merchantPhone.replace(/[^\d]/g, "");
    const msg = `Bonjour TrigoTour,\n\nJe souhaite réinitialiser mon mot de passe.\n\nMon email: ${email}\n\nMerci de m'envoyer le code de réinitialisation par WhatsApp.`;
    window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) { setError("Les mots de passe ne correspondent pas"); return; }
    if (newPassword.length < 6) { setError("Le mot de passe doit contenir au moins 6 caracteres"); return; }
    setLoading(true); setError("");
    try {
      await api.post("/auth/confirm-reset", { email, code, new_password: newPassword });
      setStep("success");
    } catch (err) {
      setError(err.response?.data?.detail || "Code invalide ou expire");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-[#CBD5E1] p-8">
          <Link to="/login" className="inline-flex items-center gap-1 text-sm text-[#64748B] hover:text-[#0A2540] mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour a la connexion
          </Link>

          <div className="text-center mb-6">
            <img src="/trigo-logo.png" alt="TrigoTour" className="w-12 h-12 mx-auto mb-3 object-contain" />
            <h1 className="text-2xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit' }}>
              {step === "success" ? "Mot de passe reinitialise" : "Mot de passe oublie"}
            </h1>
          </div>

          {error && (
            <div className="bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-lg p-3 mb-4">
              <p className="text-sm text-[#EF4444]">{error}</p>
            </div>
          )}

          {step === "email" && (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <p className="text-sm text-[#64748B] text-center">
                Entrez votre adresse email pour recevoir un code de reinitialisation
              </p>
              <div>
                <Label className="text-[#0A2540]">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-2.5 w-4 h-4 text-[#CBD5E1]" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    placeholder="votre@email.com" className="pl-10 border-[#CBD5E1]" data-testid="reset-email" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] text-white hover:bg-[#081b2e]" data-testid="reset-request-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Envoyer le code
              </Button>
            </form>
          )}

          {step === "code" && (
            <form onSubmit={handleConfirmReset} className="space-y-4">
              <div className="bg-[#25D366]/10 border border-[#25D366]/30 rounded-lg p-4 text-center">
                <MessageCircle className="w-8 h-8 text-[#25D366] mx-auto mb-2" />
                <p className="text-sm font-semibold text-[#0A2540] mb-2">Demandez votre code via WhatsApp</p>
                <p className="text-xs text-[#64748B] mb-3">
                  Cliquez sur le bouton ci-dessous pour envoyer une demande à l'équipe TrigoTour. Vous recevrez votre code en retour par WhatsApp.
                </p>
                <Button
                  type="button"
                  onClick={openWhatsAppForCode}
                  disabled={!merchantPhone}
                  className="w-full bg-[#25D366] text-white hover:bg-[#1FAE53] h-10"
                  data-testid="open-whatsapp-reset"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Demander le code via WhatsApp
                </Button>
                {!merchantPhone && (
                  <p className="text-[10px] text-[#EF4444] mt-2">Numéro support indisponible. Veuillez contacter l'administrateur.</p>
                )}
              </div>
              <p className="text-xs text-[#64748B] text-center">
                Une fois le code reçu par WhatsApp, saisissez-le ci-dessous avec votre nouveau mot de passe.
              </p>
              <div>
                <Label className="text-[#0A2540]">Code de reinitialisation</Label>
                <div className="relative mt-1">
                  <KeyRound className="absolute left-3 top-2.5 w-4 h-4 text-[#CBD5E1]" />
                  <Input type="text" value={code} onChange={(e) => setCode(e.target.value)} required
                    placeholder="123456" maxLength={6} className="pl-10 border-[#CBD5E1] tracking-widest text-center text-lg font-semibold" data-testid="reset-code-input" />
                </div>
              </div>
              <div>
                <Label className="text-[#0A2540]">Nouveau mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-[#CBD5E1]" />
                  <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required
                    placeholder="Min. 6 caracteres" className="pl-10 border-[#CBD5E1]" data-testid="reset-new-password" />
                </div>
              </div>
              <div>
                <Label className="text-[#0A2540]">Confirmer le mot de passe</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-2.5 w-4 h-4 text-[#CBD5E1]" />
                  <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                    placeholder="Confirmer" className="pl-10 border-[#CBD5E1]" data-testid="reset-confirm-password" />
                </div>
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-[#0A2540] text-white hover:bg-[#081b2e]" data-testid="reset-confirm-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                Reinitialiser le mot de passe
              </Button>
            </form>
          )}

          {step === "success" && (
            <div className="text-center py-4">
              <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
              <p className="text-sm text-[#64748B] mb-6">Votre mot de passe a ete reinitialise avec succes.</p>
              <Link to="/login">
                <Button className="w-full bg-[#FACC15] text-[#0A2540] hover:bg-[#EAB308] font-semibold" data-testid="back-to-login-btn">
                  Se connecter
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
